/**
 * `demo/phoneFramePool.ts` - the member-app frames of the demo (#2824).
 *
 * The frames are plain elements in `document.body`, outside React, so no page
 * change ever removes them or loads them again. There is one frame per
 * recently viewed person (at most `MAX_FRAMES`): a new pick of a person that
 * already has a frame only shows that frame, with no reload and no sign-in.
 * The app shell warms the first frame as soon as a demo starts. The phone
 * dock lays the active frame over the phone's screen while the phone is open
 * and hides every frame (never removes one) while folded.
 */
import { DEMO_PHONE_MEMBER_PARAM } from "../../lib/demoPhone";

export const FRAME_WIDTH_PX = 360;
/** The active frame and the ones kept warm for the people viewed last. */
export const MAX_FRAMES = 3;

interface Entry {
  key: string;
  el: HTMLIFrameElement;
  loadedSrc: string | null;
  used: number;
}

const entries = new Map<string, Entry>();
let activeKey: string | null = null;
let tick = 0;
const loadListeners = new Set<(src: string) => void>();

export const HIDDEN_STYLE: Partial<CSSStyleDeclaration> = {
  position: "fixed",
  left: "-10000px",
  top: "0px",
  width: `${FRAME_WIDTH_PX}px`,
  height: "640px",
  visibility: "hidden",
  pointerEvents: "none",
  transform: "none",
  // Level with the phone aside (z-40) and the right-edge panels (SideDrawer, z-40):
  // the frame is in <body> after the aside and before any panel portal, so it
  // paints over the phone's screen and under a panel that covers the phone.
  zIndex: "40",
  border: "0",
};

/** The person a frame address names. */
export function frameKey(src: string): string {
  const q = src.split("?")[1] ?? "";
  return new URLSearchParams(q).get(DEMO_PHONE_MEMBER_PARAM) ?? src;
}

function hide(el: HTMLIFrameElement): void {
  Object.assign(el.style, HIDDEN_STYLE);
  el.setAttribute("aria-hidden", "true");
  el.tabIndex = -1;
}

function create(key: string): Entry {
  const el = document.createElement("iframe");
  el.title = "Member app";
  el.setAttribute("data-testid", "demo-phone-frame");
  el.className = "bg-bg";
  hide(el);
  const entry: Entry = { key, el, loadedSrc: null, used: ++tick };
  el.addEventListener("load", () => {
    entry.loadedSrc = el.getAttribute("src");
    for (const l of loadListeners) l(entry.loadedSrc ?? "");
  });
  document.body.appendChild(el);
  entries.set(key, entry);
  // Over the cap: drop the frame viewed longest ago (never the active one).
  while (entries.size > MAX_FRAMES) {
    let oldest: Entry | null = null;
    for (const e of entries.values()) {
      if (e.key === activeKey || e.key === key) continue;
      if (!oldest || e.used < oldest.used) oldest = e;
    }
    if (!oldest) break;
    oldest.el.remove();
    entries.delete(oldest.key);
  }
  return entry;
}

function point(entry: Entry, src: string): void {
  if (entry.el.getAttribute("src") !== src) {
    entry.loadedSrc = null;
    entry.el.setAttribute("src", src);
  }
}

/** The active frame, created on first use. */
export function phoneFrame(): HTMLIFrameElement {
  const live = activeKey ? entries.get(activeKey) : undefined;
  return (live ?? create(activeKey ?? "_")).el;
}

/** The active frame if there is one, without creating any. */
export function activePhoneFrame(): HTMLIFrameElement | null {
  const live = activeKey ? entries.get(activeKey) : undefined;
  return live?.el.isConnected ? live.el : null;
}

/**
 * One person's frame, only while it is the active (visible) one. Null when
 * the person has no frame or another person's frame is the active one.
 */
export function activeFrameOf(memberId: string): HTMLIFrameElement | null {
  const active = activePhoneFrame();
  if (!active || activeKey !== memberId) return null;
  return active.style.visibility === "hidden" ? null : active;
}

/** Make the person's frame the active one, pointed at the address. */
export function setPhoneFrameSrc(src: string): HTMLIFrameElement {
  const key = frameKey(src);
  let entry = entries.get(key);
  if (!entry?.el.isConnected) {
    entries.delete(key);
    activeKey = key;
    entry = create(key);
  }
  if (activeKey !== key) {
    const prev = activeKey ? entries.get(activeKey) : undefined;
    if (prev) hide(prev.el);
    activeKey = key;
  }
  entry.used = ++tick;
  point(entry, src);
  return entry.el;
}

/** Load a person's frame in the background, hidden, without showing it. */
export function warmPhoneFrame(src: string): void {
  const key = frameKey(src);
  if (key === activeKey) return;
  let entry = entries.get(key);
  if (!entry) {
    // A warm frame is the newest of the others but never pushes out the active one.
    entry = create(key);
    entry.used = 0;
  }
  point(entry, src);
}

/** True when a frame finished loading this address. */
export function phoneFrameLoaded(src: string): boolean {
  const entry = entries.get(frameKey(src));
  return !!entry && entry.loadedSrc === src && entry.el.getAttribute("src") === src;
}

/** True when a frame holds this person (loaded or loading). */
export function phoneFrameHolds(src: string): boolean {
  return entries.get(frameKey(src))?.el.getAttribute("src") === src;
}

export function onPhoneFrameLoad(listener: (src: string) => void): () => void {
  loadListeners.add(listener);
  return () => {
    loadListeners.delete(listener);
  };
}

/** Tests: drop every frame. */
export function resetPhoneFrame(): void {
  for (const e of entries.values()) e.el.remove();
  entries.clear();
  activeKey = null;
}
